/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';
import { up } from 'styled-breakpoints';
import { ButtonReset, Focus } from '@fenderdigital/ui/utils/css';
import Link from '../Link';

const SectionCtaLinkWrapper = styled.div`
  a {
    :hover {
      color: ${({ theme: { colors } }): string => colors.black};
    }
  }
`;

interface SectionTitleProps {
  title?: string;
  cta?: string;
  dataId?: string;
  link?: string;
  titleStyles?: string;
  ctaStyles?: string;
  isBorder?: boolean;
  isMainTitle?: boolean;
  handleClick?: () => void;
}

interface SectionTitleStyleProps {
  isBorder?: boolean;
  isMainTitle?: boolean;
}

const messages = defineMessages({
  title: {
    id: 'sectionTitle.title',
    defaultMessage: '{title}',
  },
  cta: {
    id: 'sectionTitle.cta',
    defaultMessage: '{cta}',
  },
});

const SectionWrapper = styled.div<SectionTitleStyleProps>`
  width: 100%;
  padding-bottom: ${({ isBorder }): string => (isBorder ? '0.5rem' : '0px')};
  margin-bottom: ${({ isBorder, theme: { spacing } }): string =>
    isBorder ? spacing.sp0 : spacing.md1};
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: ${({ isBorder, theme: { colors } }): string =>
    isBorder ? `1px solid ${colors.moonGray}` : 'none'};
`;

const SectionTitleText = styled.h2<SectionTitleStyleProps>`
  margin: 0px;
  font-size: ${({ isMainTitle, theme: { fontSizes } }): string =>
    isMainTitle ? fontSizes.f8 : fontSizes.f6};
  line-height: 1.25;
  font-family: ${({ theme: { fonts } }): string => fonts.futuraHeavy};

  ${up('tablet-portrait')} {
    font-size: ${({ isMainTitle, theme: { fontSizes } }): string =>
      isMainTitle ? fontSizes.f9 : fontSizes.f6};
  }
  ${up('laptop')} {
    font-size: ${({ isMainTitle, theme: { fontSizes } }): string =>
      isMainTitle ? fontSizes.f10 : fontSizes.f8};
  }
`;

const SectionCta = styled.button<SectionTitleStyleProps>`
  ${ButtonReset};
  ${Focus};

  margin: 0px;
  margin-left: 10px;
  font-size: ${({ theme: { fontSizes } }): string => fontSizes.f3};
  color: ${({ theme: { colors } }): string => colors.black};
  white-space: nowrap;
  cursor: pointer;
  border-bottom: 2px solid #e5e5e5;
  font-family: ${({ theme: { fonts } }): string => fonts.futuraDemi};

  &:hover,
  &:focus {
    border-bottom-color: ${({ theme: { colors } }): string => colors.black};
  }
`;

const SectionTitle: React.FC<SectionTitleProps> = ({
  title = 'Section Title',
  cta = '',
  dataId,
  link = '#',
  titleStyles = '',
  ctaStyles = '',
  isBorder = true,
  isMainTitle = false,
  handleClick,
  children,
}) => {
  return (
    <SectionWrapper isBorder={isBorder} data-id={dataId}>
      <SectionTitleText className={titleStyles} isMainTitle={isMainTitle}>
        {children || <FormattedMessage {...messages.title} values={{ title }} />}
      </SectionTitleText>
      {handleClick && cta && (
        <SectionCta onClick={handleClick}>
          <FormattedMessage {...messages.cta} values={{ cta }} />
        </SectionCta>
      )}
      {!handleClick && cta && (
        <SectionCtaLinkWrapper>
          <SectionCta as={Link} to={link} className={ctaStyles}>
            <FormattedMessage {...messages.cta} values={{ cta }} />
          </SectionCta>
        </SectionCtaLinkWrapper>
      )}
    </SectionWrapper>
  );
};

export default SectionTitle;
